// File: rbqmNavbar.js
import { defineComponent, ref, computed, onMounted, onUnmounted, watch  } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'RBQMNavbar',
  emits: ['sub-category-click'],
  props: {
    // receives from parent: :selectedsite="selectedSiteId"
    selectedsite: { type: String, default: '' }
  },
  setup(props, { emit }) {
    const store = useStore();
    const baseurl = process.env.VUE_APP_Service_URL;
    const { t } = useI18n({ useScope: 'global' });

    // Raw API list
    const ractlist = ref([]);
    // Open/closed state maps
    const mainMenuState = ref({});
    const subMenuState  = ref({});
     const selectedSubKey = ref(null);

    // Media-query listener for collapsing navbar
    const mql = window.matchMedia('(min-width:769px)');
    function initNav() {
      const isWide = window.innerWidth > 769;
      document.body.classList.toggle('mini-navbar', !isWide);
      const menuEl = document.getElementById('mobilemenu');
      if (menuEl) {
        if (isWide) {
          menuEl.classList.add('navbar-default', 'navbar-static-side');
        } else {
          menuEl.classList.remove('navbar-default', 'navbar-static-side');
        }
      }
    }
    function onSubCategoryClick(catName, subName) {
      const key = `${catName}-${subName}`;
      selectedSubKey.value = key;
  const items = collectSubcategoryDetails(catName, subName);
  emit('sub-category-click', {
    category: catName,
    subCategory: subName,
    items // now: [{ discussionDetails, score, impactPoint, probabilityPoint, detectabilityPoint, riskLevelColor, orderNo, id }]
  });
}

// ...inside getRactData(), after ractlist is refreshed:
emit('sub-category-click', { category: null, subCategory: null, items: [] });
    function collectSubcategoryDetails(catName, subName) {
  const list = Array.isArray(ractlist.value) ? ractlist.value : [];
  const out = [];

  for (let i = 0; i < list.length; i++) {
    const item = list[i] || {};
    const cat = (typeof item.category === 'string' ? item.category.trim() : '') || 'Uncategorized';
    const sub = (typeof item.subCategory === 'string' ? item.subCategory.trim() : '') || 'General';
    if (cat !== catName || sub !== subName) continue;

    const discussionDetails = (typeof item.discussionDetails === 'string' ? item.discussionDetails.trim() : '');
    const riskIndicator = (typeof item.riskIndicator === 'string' ? item.riskIndicator.trim() : '');
    

    out.push({
      riskIndicator,
      discussionDetails,
      score: item.score,
      impactPoint: item.impactPoint,
      probabilityPoint: item.probabilityPoint,
      detectabilityPoint: item.detectabilityPoint,
      riskLevelColor: item.riskLevelColor,
      orderNo: item.categoryId, 
      id: item.riskId
    });
  }
  return out;
}
    // Fetch RBQM data
    async function getRactData() {
      try {
        const studyId = store.getters.getStudyIs;
         const dataScopeId =
          (props.selectedsite && props.selectedsite.trim() !== '')
            ? props.selectedsite.trim()
            : studyId;
          const dataScope =
          (props.selectedsite && props.selectedsite.trim() !== '')
            ? "Site"
            : "Study";
        const idtoken = store.getters.getIdToken;
        const res = await axios.get(
          `${baseurl}/forms/rbqm/getriskitem?StudyId=${studyId}&pageindex=1&pagesize=200&SortProperty=createdDate&SortOrder=desc&DataScopeId=${dataScopeId}&DataScope=${dataScope}`,
          { headers: { Authorization: `Bearer ${idtoken}` } }
        );
        ractlist.value = Array.isArray(res.data.data.entities) ? res.data.data.entities : [];
        emit('sub-category-click', { category: null, subCategory: null, items: [] });
      } catch (e) {
        console.error('Error fetching RBQM data:', e);
      }
    }

    // Group API data into menu structure
    const groupedData = computed(function () {
  const map = Object.create(null);
  const list = Array.isArray(ractlist.value) ? ractlist.value : [];

  for (let i = 0; i < list.length; i++) {
    const item = list[i] || {};

    // Safe normalization
    const catRaw = (typeof item.category === 'string') ? item.category : '';
    const subRaw = (typeof item.subCategory === 'string') ? item.subCategory : '';
    const detRaw = (typeof item.discussionDetails === 'string') ? item.discussionDetails : '';

    const catKey = catRaw.trim() || 'Uncategorized';
    const subKey = subRaw.trim() || 'General';
    const detail = detRaw.trim();

    // Score (number) if valid
    const s = Number(item.score);
    const hasScore = Number.isFinite(s);

    // Init category bucket
    if (!map[catKey]) {
      map[catKey] = {
        name: catKey,
        sum: 0,
        count: 0,
        subs: Object.create(null)
      };
    }
    if (hasScore) {
      map[catKey].sum += s;
      map[catKey].count += 1;
    }

    // Init subcategory bucket
    if (!map[catKey].subs[subKey]) {
      map[catKey].subs[subKey] = {
        details: new Set(),
        sum: 0,
        count: 0
      };
    }
    if (hasScore) {
      map[catKey].subs[subKey].sum += s;
      map[catKey].subs[subKey].count += 1;
    }

    // Add detail (if present)
    if (detail) {
      map[catKey].subs[subKey].details.add(detail);
    }
  }

  // Build final array
  const result = [];
  const catNames = Object.keys(map);

  for (let c = 0; c < catNames.length; c++) {
    const catName = catNames[c];
    const catObj = map[catName];

    const catAvg = catObj.count ? catObj.sum / catObj.count : NaN;
    const subArr = [];
    const subNames = Object.keys(catObj.subs);

    for (let s = 0; s < subNames.length; s++) {
      const subName = subNames[s];
      const subObj = catObj.subs[subName];

      const detailsArr = [];
      subObj.details.forEach(v => detailsArr.push(v));

      const subAvg = subObj.count ? subObj.sum / subObj.count : NaN;

      subArr.push({
        name: subName,
        details: detailsArr,
        avgScore: subAvg,
        borderColor: scoreToColor(subAvg)
      });
    }

    result.push({
      name: catObj.name,
      avgScore: catAvg,
      borderColor: scoreToColor(catAvg),
      subcategories: subArr
    });
  }

  return result;
});

// Color rule for averages
function scoreToColor(avg) {
  if (!Number.isFinite(avg)) return 'transparent';
  if (avg < 10) return '#87d37c';
  if (avg < 20) return '#f5d76e';
  return '#F22613';
}
    // Toggle handlers
    function toggleCategory(name) {
      mainMenuState.value[name] = !mainMenuState.value[name];
    }
    function toggleSubCategory(cat, sub) {
      const key = `${cat}-${sub}`;
      subMenuState.value[key] = !subMenuState.value[key];
    }

    onMounted(() => {
      mql.addListener(initNav);
      initNav();
      getRactData();
       window.addEventListener("studyIdChanged", async (event) => {
        initNav();
      getRactData();
      });
    });
    onUnmounted(() => {
      mql.removeListener(initNav);
    });

    watch(() => props.selectedsite, () => {
      getRactData();
    });

    return { t, groupedData, mainMenuState, subMenuState,onSubCategoryClick, toggleCategory, toggleSubCategory, selectedSubKey };
  }
});